import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Building2, Users, Home } from "lucide-react";
import type { RegisteredLender, RegisteredBroker, Partner } from "@shared/schema";
import { LOAN_TYPES_SOUGHT, type LoanTypeSought } from "@shared/schema";
import { formatPhone } from "@/lib/format-phone";

const LOAN_TYPE_LABELS: Record<LoanTypeSought, string> = {
  conventional: "Conventional",
  fha: "FHA",
  va: "VA",
  usda: "USDA",
  jumbo: "Jumbo",
  dscr: "DSCR",
  hard_money: "Hard Money",
  cash_out_refinance: "Cash-Out Refinance",
  heloc: "HELOC",
  other: "Other",
};

interface ClosingTeamModalProps {
  open: boolean;
  propertyId?: number | null;
  acquisitionId?: number | null;
  capturedAtStage: string;
  stageLabel: string;
  propertyAddress?: string;
  onSuccess: () => void;
}

const STAGE_LABELS: Record<string, string> = {
  offer_submitted: "Offer Submitted",
  offer_accepted: "Offer Accepted",
  clear_to_close: "Clear to Close",
  closed: "Closed",
  renovations: "Renovation / Acquired",
  active: "Active",
};

export function ClosingTeamModal({
  open,
  propertyId,
  acquisitionId,
  capturedAtStage,
  stageLabel,
  propertyAddress,
  onSuccess,
}: ClosingTeamModalProps) {
  const { toast } = useToast();

  const { data: lenders = [] } = useQuery<RegisteredLender[]>({
    queryKey: ["/api/app/referrals/lenders"],
    enabled: open,
  });

  const { data: brokers = [] } = useQuery<RegisteredBroker[]>({
    queryKey: ["/api/app/referrals/brokers"],
    enabled: open,
  });

  const { data: realtors = [] } = useQuery<Partner[]>({
    queryKey: ["/api/app/referrals/realtors"],
    enabled: open,
  });

  const [lenderSelection, setLenderSelection] = useState<string>("");
  const [lenderContact, setLenderContact] = useState({ name: "", email: "", phone: "", company: "" });

  const [brokerSelection, setBrokerSelection] = useState<string>("");
  const [brokerContact, setBrokerContact] = useState({ name: "", email: "", phone: "", company: "" });

  const [realtorSelection, setRealtorSelection] = useState<string>("");
  const [realtorContact, setRealtorContact] = useState({ name: "", email: "", phone: "", company: "" });

  const [loanTypeSought, setLoanTypeSought] = useState<string>("");
  const [estimatedValue, setEstimatedValue] = useState<string>("");

  const [errors, setErrors] = useState<Record<string, string>>({});

  const selectedLender = lenderSelection && lenderSelection !== "other"
    ? lenders.find(l => l.id === parseInt(lenderSelection))
    : null;
  const selectedBroker = brokerSelection && brokerSelection !== "other"
    ? brokers.find(b => b.id === parseInt(brokerSelection))
    : null;
  const selectedRealtor = realtorSelection && realtorSelection !== "other"
    ? realtors.find(r => r.id === parseInt(realtorSelection))
    : null;

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!lenderSelection) errs.lender = "Please select a lender";
    if (!brokerSelection) errs.broker = "Please select a broker/wholesaler";

    if (lenderSelection === "other") {
      if (!lenderContact.name.trim()) errs.lenderName = "Name is required";
      if (!lenderContact.email.trim()) errs.lenderEmail = "Email is required";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lenderContact.email)) errs.lenderEmail = "Valid email required";
    }

    if (brokerSelection === "other") {
      if (!brokerContact.name.trim()) errs.brokerName = "Name is required";
      if (!brokerContact.email.trim()) errs.brokerEmail = "Email is required";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(brokerContact.email)) errs.brokerEmail = "Valid email required";
    }

    // Realtor is optional — only validate the manual-entry fields when the host
    // explicitly chose to enter an unregistered realtor.
    if (realtorSelection === "other") {
      if (!realtorContact.name.trim()) errs.realtorName = "Name is required";
      if (realtorContact.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(realtorContact.email)) errs.realtorEmail = "Valid email required";
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const lenderIsRegistered = lenderSelection !== "other";
      const brokerIsRegistered = brokerSelection !== "other";

      const lenderData = selectedLender
        ? { lenderId: selectedLender.id, lenderContactName: selectedLender.contactName, lenderEmail: selectedLender.email, lenderPhone: selectedLender.phone, lenderCompanyName: selectedLender.companyName }
        : { lenderId: null, lenderContactName: lenderContact.name, lenderEmail: lenderContact.email, lenderPhone: lenderContact.phone, lenderCompanyName: lenderContact.company };

      const brokerData = selectedBroker
        ? { brokerId: selectedBroker.id, brokerContactName: selectedBroker.contactName, brokerEmail: selectedBroker.email, brokerPhone: selectedBroker.phone, brokerCompanyName: selectedBroker.companyName }
        : { brokerId: null, brokerContactName: brokerContact.name, brokerEmail: brokerContact.email, brokerPhone: brokerContact.phone, brokerCompanyName: brokerContact.company };

      // Realtor is optional. A registered realtor (a `partners` row) sets
      // realtorPartnerId + realtorIsRegistered so the 25% referral invoice can
      // be generated at close; an unregistered realtor is stored as a contact.
      let realtorData: Record<string, unknown> = {};
      if (selectedRealtor) {
        realtorData = {
          realtorPartnerId: selectedRealtor.id,
          realtorIsRegistered: true,
          realtorContactName: selectedRealtor.contactName,
          realtorEmail: selectedRealtor.email,
          realtorPhone: selectedRealtor.phone,
          realtorCompanyName: selectedRealtor.companyName,
        };
      } else if (realtorSelection === "other") {
        realtorData = {
          realtorPartnerId: null,
          realtorIsRegistered: false,
          realtorContactName: realtorContact.name,
          realtorEmail: realtorContact.email,
          realtorPhone: realtorContact.phone,
          realtorCompanyName: realtorContact.company,
        };
      }

      const payload = {
        propertyId,
        acquisitionId,
        capturedAtStage,
        lenderIsRegistered,
        brokerIsRegistered,
        ...lenderData,
        ...brokerData,
        ...realtorData,
        loanTypeSought: loanTypeSought || null,
        estimatedValue: estimatedValue ? estimatedValue.replace(/[^0-9.]/g, "") : null,
      };

      return apiRequest("POST", "/api/app/referrals/closing-team", payload);
    },
    onSuccess: () => {
      if (propertyId) {
        queryClient.invalidateQueries({ queryKey: ["/api/app/referrals/closing-team/property", propertyId] });
      }
      toast({ title: "Closing team saved", description: "Information has been recorded." });
      onSuccess();
    },
    onError: (err: any) => {
      const msg = err?.message || "Failed to save closing team";
      toast({ title: "Error", description: msg, variant: "destructive" });
    },
  });

  function handleSubmit() {
    if (!validate()) return;
    saveMutation.mutate();
  }

  const displayStage = STAGE_LABELS[capturedAtStage] || stageLabel || capturedAtStage;

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto [&>button:last-child]:hidden"
        onPointerDownOutside={e => e.preventDefault()}
        onEscapeKeyDown={e => e.preventDefault()}
        data-testid="dialog-closing-team"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-full bg-brand/10 flex items-center justify-center">
              <Users className="h-4 w-4 text-brand" />
            </div>
            <DialogTitle className="text-lg">Capture Closing Team</DialogTitle>
          </div>
          <DialogDescription className="text-sm text-gray-500">
            This property is advancing to <strong>{displayStage}</strong>
            {propertyAddress ? ` — ${propertyAddress}` : ""}.
            Please record the lender and broker who facilitated this transaction.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          {/* Lender Section */}
          <div className="rounded-lg border border-gray-200 p-4 space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <Building2 className="h-4 w-4 text-brand" />
              <span className="font-semibold text-sm text-gray-900">Lender</span>
            </div>

            <div>
              <Label className="text-xs text-gray-600 mb-1 block">Select Lender</Label>
              <Select value={lenderSelection} onValueChange={setLenderSelection} data-testid="select-lender">
                <SelectTrigger className="w-full" data-testid="trigger-lender">
                  <SelectValue placeholder="Choose a lender…" />
                </SelectTrigger>
                <SelectContent>
                  {lenders.map(l => (
                    <SelectItem key={l.id} value={String(l.id)} data-testid={`lender-option-${l.id}`}>
                      {l.contactName} — {l.companyName}
                    </SelectItem>
                  ))}
                  <SelectItem value="other">Other (not yet registered)</SelectItem>
                </SelectContent>
              </Select>
              {errors.lender && <p className="text-red-500 text-xs mt-1">{errors.lender}</p>}
            </div>

            {lenderSelection === "other" && (
              <div className="space-y-3 pt-2 border-t border-gray-100">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-600 mb-1 block">Contact Name *</Label>
                    <Input
                      value={lenderContact.name}
                      onChange={e => setLenderContact(p => ({ ...p, name: e.target.value }))}
                      placeholder="Jane Smith"
                      data-testid="input-lender-name"
                    />
                    {errors.lenderName && <p className="text-red-500 text-xs mt-1">{errors.lenderName}</p>}
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600 mb-1 block">Company</Label>
                    <Input
                      value={lenderContact.company}
                      onChange={e => setLenderContact(p => ({ ...p, company: e.target.value }))}
                      placeholder="Acme Lending"
                      data-testid="input-lender-company"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-600 mb-1 block">Email *</Label>
                    <Input
                      type="email"
                      value={lenderContact.email}
                      onChange={e => setLenderContact(p => ({ ...p, email: e.target.value }))}
                      placeholder="jane@acmelending.com"
                      data-testid="input-lender-email"
                    />
                    {errors.lenderEmail && <p className="text-red-500 text-xs mt-1">{errors.lenderEmail}</p>}
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600 mb-1 block">Phone</Label>
                    <Input
                      value={lenderContact.phone}
                      onChange={e => setLenderContact(p => ({ ...p, phone: formatPhone(e.target.value) }))}
                      placeholder="(555) 000-0000"
                      data-testid="input-lender-phone"
                    />
                  </div>
                </div>
              </div>
            )}

            {selectedLender && (
              <div className="bg-brand/5 rounded-md p-3 text-sm text-gray-700">
                <p className="font-medium">{selectedLender.contactName}</p>
                <p className="text-gray-500">{selectedLender.companyName} · {selectedLender.email}</p>
                {selectedLender.phone && <p className="text-gray-500">{selectedLender.phone}</p>}
              </div>
            )}
          </div>

          {/* Broker Section */}
          <div className="rounded-lg border border-gray-200 p-4 space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <Building2 className="h-4 w-4 text-green-600" />
              <span className="font-semibold text-sm text-gray-900">Broker / Wholesaler</span>
            </div>

            <div>
              <Label className="text-xs text-gray-600 mb-1 block">Select Broker</Label>
              <Select value={brokerSelection} onValueChange={setBrokerSelection} data-testid="select-broker">
                <SelectTrigger className="w-full" data-testid="trigger-broker">
                  <SelectValue placeholder="Choose a broker…" />
                </SelectTrigger>
                <SelectContent>
                  {brokers.map(b => (
                    <SelectItem key={b.id} value={String(b.id)} data-testid={`broker-option-${b.id}`}>
                      {b.contactName} — {b.companyName}
                    </SelectItem>
                  ))}
                  <SelectItem value="other">Other (not yet registered)</SelectItem>
                </SelectContent>
              </Select>
              {errors.broker && <p className="text-red-500 text-xs mt-1">{errors.broker}</p>}
            </div>

            {brokerSelection === "other" && (
              <div className="space-y-3 pt-2 border-t border-gray-100">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-600 mb-1 block">Contact Name *</Label>
                    <Input
                      value={brokerContact.name}
                      onChange={e => setBrokerContact(p => ({ ...p, name: e.target.value }))}
                      placeholder="John Doe"
                      data-testid="input-broker-name"
                    />
                    {errors.brokerName && <p className="text-red-500 text-xs mt-1">{errors.brokerName}</p>}
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600 mb-1 block">Company</Label>
                    <Input
                      value={brokerContact.company}
                      onChange={e => setBrokerContact(p => ({ ...p, company: e.target.value }))}
                      placeholder="Acme Brokerage"
                      data-testid="input-broker-company"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-600 mb-1 block">Email *</Label>
                    <Input
                      type="email"
                      value={brokerContact.email}
                      onChange={e => setBrokerContact(p => ({ ...p, email: e.target.value }))}
                      placeholder="john@acmebrokerage.com"
                      data-testid="input-broker-email"
                    />
                    {errors.brokerEmail && <p className="text-red-500 text-xs mt-1">{errors.brokerEmail}</p>}
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600 mb-1 block">Phone</Label>
                    <Input
                      value={brokerContact.phone}
                      onChange={e => setBrokerContact(p => ({ ...p, phone: formatPhone(e.target.value) }))}
                      placeholder="(555) 000-0000"
                      data-testid="input-broker-phone"
                    />
                  </div>
                </div>
              </div>
            )}

            {selectedBroker && (
              <div className="bg-green-50 rounded-md p-3 text-sm text-gray-700">
                <p className="font-medium">{selectedBroker.contactName}</p>
                <p className="text-gray-500">{selectedBroker.companyName} · {selectedBroker.email}</p>
                {selectedBroker.phone && <p className="text-gray-500">{selectedBroker.phone}</p>}
              </div>
            )}
          </div>

          {/* Realtor Section */}
          <div className="rounded-lg border border-gray-200 p-4 space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <Home className="h-4 w-4 text-brand-accent" />
              <span className="font-semibold text-sm text-gray-900">Realtor</span>
              <span className="text-xs text-gray-400">(optional)</span>
            </div>

            <div>
              <Label className="text-xs text-gray-600 mb-1 block">Select Realtor</Label>
              <Select value={realtorSelection} onValueChange={setRealtorSelection} data-testid="select-realtor">
                <SelectTrigger className="w-full" data-testid="trigger-realtor">
                  <SelectValue placeholder="Choose a realtor…" />
                </SelectTrigger>
                <SelectContent>
                  {realtors.map(r => (
                    <SelectItem key={r.id} value={String(r.id)} data-testid={`realtor-option-${r.id}`}>
                      {r.contactName} — {r.companyName}
                    </SelectItem>
                  ))}
                  <SelectItem value="other">Other (not yet registered)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {realtorSelection === "other" && (
              <div className="space-y-3 pt-2 border-t border-gray-100">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-600 mb-1 block">Contact Name *</Label>
                    <Input
                      value={realtorContact.name}
                      onChange={e => setRealtorContact(p => ({ ...p, name: e.target.value }))}
                      placeholder="Sam Agent"
                      data-testid="input-realtor-name"
                    />
                    {errors.realtorName && <p className="text-red-500 text-xs mt-1">{errors.realtorName}</p>}
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600 mb-1 block">Company</Label>
                    <Input
                      value={realtorContact.company}
                      onChange={e => setRealtorContact(p => ({ ...p, company: e.target.value }))}
                      placeholder="Acme Realty"
                      data-testid="input-realtor-company"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-600 mb-1 block">Email</Label>
                    <Input
                      type="email"
                      value={realtorContact.email}
                      onChange={e => setRealtorContact(p => ({ ...p, email: e.target.value }))}
                      placeholder="sam@acmerealty.com"
                      data-testid="input-realtor-email"
                    />
                    {errors.realtorEmail && <p className="text-red-500 text-xs mt-1">{errors.realtorEmail}</p>}
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600 mb-1 block">Phone</Label>
                    <Input
                      value={realtorContact.phone}
                      onChange={e => setRealtorContact(p => ({ ...p, phone: formatPhone(e.target.value) }))}
                      placeholder="(555) 000-0000"
                      data-testid="input-realtor-phone"
                    />
                  </div>
                </div>
              </div>
            )}

            {selectedRealtor && (
              <div className="bg-brand-accent/5 rounded-md p-3 text-sm text-gray-700">
                <p className="font-medium">{selectedRealtor.contactName}</p>
                <p className="text-gray-500">{selectedRealtor.companyName} · {selectedRealtor.email}</p>
                {selectedRealtor.phone && <p className="text-gray-500">{selectedRealtor.phone}</p>}
              </div>
            )}
          </div>

          {/* Loan Details Section */}
          <div className="rounded-lg border border-gray-200 p-4 space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <Building2 className="h-4 w-4 text-amber-600" />
              <span className="font-semibold text-sm text-gray-900">Loan Details</span>
              <span className="text-xs text-gray-400">(optional)</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-gray-600 mb-1 block">Loan Type Sought</Label>
                <Select value={loanTypeSought} onValueChange={setLoanTypeSought}>
                  <SelectTrigger className="w-full" data-testid="trigger-loan-type">
                    <SelectValue placeholder="Select loan type…" />
                  </SelectTrigger>
                  <SelectContent>
                    {LOAN_TYPES_SOUGHT.map(t => (
                      <SelectItem key={t} value={t} data-testid={`loan-type-option-${t}`}>
                        {LOAN_TYPE_LABELS[t]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-gray-600 mb-1 block">Estimated Property Value</Label>
                <Input
                  inputMode="decimal"
                  value={estimatedValue}
                  onChange={e => setEstimatedValue(e.target.value.replace(/[^0-9.]/g, ""))}
                  placeholder="450000"
                  data-testid="input-estimated-value"
                />
              </div>
            </div>
          </div>

          <p className="text-xs text-amber-600 bg-amber-50 rounded-md px-3 py-2">
            This information is required before proceeding. It cannot be skipped.
          </p>

          <Button
            className="w-full"
            onClick={handleSubmit}
            disabled={saveMutation.isPending}
            data-testid="button-save-closing-team"
          >
            {saveMutation.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</> : "Save & Continue"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
